import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { TextInput, NumberInput, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../lib/usePerformRequest";
import { ButtonLoader } from "../../../../components/Loader";

interface Transaction {
  id: string;
  user?: string;
  amount?: number;
  status?: string;
  [key: string]: any;
}

export default function NewTransaction() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      user: "",
      amount: "",
      status: "pending",
    },
    validate: {
      user: (value: string) => {
        if (!value?.trim()) return "User email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Please enter a valid email address";
        }
        return null;
      },
      amount: (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === "") {
          return "Amount is required";
        }
        const numValue = parseFloat(String(value).replace(/[^\d.]/g, ""));
        if (isNaN(numValue) || numValue <= 0) {
          return "Please enter a valid amount greater than 0";
        }
        return null;
      },
      status: (value: string) => (!value?.trim() ? "Status is required" : null),
    },
  });

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "paid", label: "Paid" },
  ];

  async function handleCreateTransaction(values: typeof form.values) {
    setIsSubmitting(true);
    try {
      const endpoint = Endpoints.CreateTransaction();
      const response = await PerformRequest<Transaction>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          user: values.user.trim(),
          amount: parseFloat(String(values.amount).replace(/[^\d.]/g, "")),
          status: values.status,
        },
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message: "Transaction created successfully!",
          color: "green",
        });
        navigate("/d/transactions");
      } else {
        const errorMessage = response?.message || "Failed to create transaction. Please try again.";
        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to create transaction. Please try again.";
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <button
          onClick={() => navigate("/d/transactions")}
          className="dashboard-page__back-button"
          type="button"
          aria-label="Go back"
        >
          <ArrowLeft size={16} />
          <span>Back to Transactions</span>
        </button>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">Create New Transaction</h2>
          <p className="dashboard-panel__subtitle" style={{ marginTop: "8px", fontSize: "13px", color: "#6b7280" }}>
            Fill in the details below to create a new transaction
          </p>
        </div>

        <form
          onSubmit={form.onSubmit(handleCreateTransaction)}
          className="dashboard-form"
          style={{ marginTop: "24px" }}
        >
          <div className="dashboard-form__field">
            <TextInput
              label="User Email"
              placeholder="user@example.com"
              type="email"
              autoComplete="email"
              disabled={isSubmitting}
              error={form.errors.user}
              required
              {...form.getInputProps("user")}
              styles={{
                label: {
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#374151",
                  marginBottom: "6px",
                },
              }}
            />
          </div>

          <div className="dashboard-form__field">
            <NumberInput
              label="Amount"
              placeholder="₦0.00"
              disabled={isSubmitting}
              error={form.errors.amount}
              prefix="₦"
              thousandSeparator=","
              min={0}
              required
              {...form.getInputProps("amount")}
              styles={{
                label: {
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#374151",
                  marginBottom: "6px",
                },
              }}
            />
          </div>

          <div className="dashboard-form__field">
            <Select
              label="Status"
              placeholder="Select transaction status"
              data={statusOptions}
              disabled={isSubmitting}
              error={form.errors.status}
              required
              {...form.getInputProps("status")}
              styles={{
                label: {
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#374151",
                  marginBottom: "6px",
                },
              }}
            />
          </div>

          <div className="dashboard-form__actions flex items-center justify-end gap-3" style={{ marginTop: "32px", paddingTop: "24px", borderTop: "1px solid #e5e7eb" }}>
            <button
              className="dashboard-button dashboard-button--secondary"
              type="button"
              onClick={() => navigate("/d/transactions")}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="dashboard-button flex items-center justify-center gap-2"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader color="white" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Save size={14} />
                  <span>Create Transaction</span>
                </>
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

